import { coberturaDeGarantia, type ModeloConCobertura } from '@conversia/shared-types';

/**
 * S-03 · Cómo se le MUESTRA la cobertura de una garantía al operador.
 *
 * El cálculo es el compartido con el servidor (`coberturaDeGarantia`); acá solo
 * se traduce a una frase y un tono para la pantalla.
 *
 * Regla: cuando falta un dato NO se dice "fuera de garantía" — se dice qué
 * falta. Un "fuera de garantía" falso le cobra a un cliente algo que le
 * correspondía gratis.
 */

export type TonoCobertura = 'ok' | 'fuera' | 'desconocido';

export interface EtiquetaCobertura {
  tono: TonoCobertura;
  texto: string;
  /**
   * Lo único que falta para poder calcular la cobertura es el PERÍODO del
   * modelo, que se carga en Configuración → Trabajos.
   *
   * Existe para que la pantalla pueda ofrecer el atajo a esa pantalla. Sin
   * esto, el aviso decía qué faltaba y dejaba al operador a pie: tenía que
   * acordarse de dónde se cargan los meses, salir de la garantía que estaba
   * abriendo e ir a buscarlo.
   *
   * Es `false` cuando lo que falta es la FECHA: llevarlo a Configuración ahí
   * sería mandarlo a una pantalla donde no va a encontrar lo que necesita.
   */
  faltaPeriodoDelModelo: boolean;
}

/** Fecha ISO → Date, o null si viene vacía o no parsea. */
function parseFecha(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function etiquetaCoberturaJob(
  job: { modelo?: string | null; fechaInstalacion?: string | null },
  modelos: ModeloConCobertura[],
  ahora: Date = new Date(),
): EtiquetaCobertura {
  const fechaInstalacion = parseFecha(job.fechaInstalacion);
  const c = coberturaDeGarantia({
    modelo: job.modelo,
    fechaInstalacion,
    modelos,
    ahora,
  });

  if (c.dentroDeCobertura === null) {
    if (fechaInstalacion == null) {
      return {
        tono: 'desconocido',
        texto: 'No se puede saber si está en garantía: falta la fecha de instalación.',
        faltaPeriodoDelModelo: false,
      };
    }
    return {
      tono: 'desconocido',
      texto:
        `No se puede saber si está en garantía: el modelo "${job.modelo ?? 'sin modelo'}" no ` +
        `tiene período cargado en Configuración → Trabajos.`,
      faltaPeriodoDelModelo: true,
    };
  }

  const dias = c.diasTranscurridos ?? 0;
  return c.dentroDeCobertura
    ? {
        tono: 'ok',
        texto: `En garantía — instalada hace ${dias} días de ${c.mesesCobertura} meses.`,
        faltaPeriodoDelModelo: false,
      }
    : {
        tono: 'fuera',
        texto: `Fuera de garantía — instalada hace ${dias} días; cubría ${c.mesesCobertura} meses.`,
        faltaPeriodoDelModelo: false,
      };
}

/** De dónde salió esta garantía: de una instalación del sistema o de antes. */
export function etiquetaOrigenGarantia(job: {
  instalacionJobId?: string | null;
  instalacionPrevia?: boolean;
}): string {
  if (job.instalacionJobId) return 'Enlazada a su instalación';
  if (job.instalacionPrevia) return 'Instalación anterior al sistema';
  return 'Sin instalación enlazada';
}
